"use client";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { defaultColors } from "@/constants/usageColors";
import { cn } from "@/lib/utils";
import { formatTokenDate } from "@/lib/dateUtils";

export interface EndpointData {
  name: string;
  id: string;
  value: number;
  percentage: number;
  color?: string;
  createdDate?: string;
  lastUsed?: string;
  status?: string;
  billing?: {
    total_articles_this_period: number;
    total_charges_this_period: number;
    applied_tier: {
      min: number;
      max: number;
      price_per_article: number;
    };
    charge_breakdown: [
      {
        tier: string;
        count: number;
        rate: number;
        charge: number;
      },
    ];
    tier_usage_percentage: number;
  };
}

export interface SubscriptionData {
  plan_name: string;
  api_key: string;
  endpoint_path: string;
  usage: number;
  percentage: number;
  subscription_start: string;
  subscription_end: string;
  quota_total?: number;
  quota_used?: number;
  color?: string;
  billing?: {
    total_articles_this_period: number;
    total_charges_this_period: number;
    applied_tier: {
      min: number;
      max: number;
      price_per_article: number;
    };
    charge_breakdown: [
      {
        tier: string;
        count: number;
        rate: number;
        charge: number;
      },
    ];
    tier_usage_percentage: number;
  };
}

interface ClientLineChartProps {
  title?: string;
  subtitle?: string;
  endpoints: EndpointData[];
  subscriptions?: SubscriptionData[];
  className?: string;
  showValues?: boolean;
  showPercentages?: boolean;
  showTable?: boolean;
  isOverview?: boolean;
  quotaTotal?: number;
  quotaUsed?: number;
  onRevokeToken?: (apiKeyId: string) => void;
  onRevokeTokenLoading?: boolean;
}

export function ClientLineChart({
  title,
  subtitle,
  endpoints,
  subscriptions = [],
  className,
  showValues = true,
  showPercentages = true,
  showTable = false,
  isOverview = false,
  quotaTotal = 0,
  quotaUsed = 0,
  onRevokeToken,
  onRevokeTokenLoading,
}: ClientLineChartProps) {
  const activeEndpoints = endpoints.filter(
    (endpoint) => endpoint.status === "Active",
  );

  const endpointsWithColors = activeEndpoints.map((endpoint, index) => ({
    ...endpoint,
    color: endpoint.color || defaultColors[index] || defaultColors[0],
  }));

  const subscriptionsWithColors = subscriptions.map((subscription, index) => ({
    ...subscription,
    color: subscription.color || defaultColors[index] || defaultColors[0],
  }));

  const totalPercentage =
    isOverview && quotaTotal > 0
      ? (quotaUsed / quotaTotal) * 100
      : endpointsWithColors.reduce(
          (sum, endpoint) => sum + endpoint.percentage,
          0,
        );

  const remainingPercentage = Math.max(0, 100 - totalPercentage);

  const dataForProgressBar =
    isOverview && quotaTotal > 0
      ? subscriptionsWithColors.map((sub) => ({
          ...sub,
          percentage: quotaTotal > 0 ? (sub.usage / quotaTotal) * 100 : 0,
        }))
      : subscriptionsWithColors;

  return (
    <div
      className={cn("bg-black-950 flex flex-col gap-5 rounded-lg", className)}
    >
      {(title || subtitle) && (
        <div className="flex flex-row items-center justify-between p-5 pb-0">
          <div className="space-y-1">
            {title && (
              <div className="text-lg font-bold -tracking-wide text-white">
                {title}
              </div>
            )}
            {subtitle && (
              <div className="text-black-500 text-xs leading-3">{subtitle}</div>
            )}
          </div>
        </div>
      )}

      {/* Segmented Progress Bar */}
      <div
        className="bg-black-900/50 flex w-auto overflow-hidden rounded"
        style={{ height: "12px" }}
      >
        {isOverview
          ? dataForProgressBar.map((item, index) => (
              <Tooltip key={`tooltip-${item.plan_name}-${index}`}>
                <TooltipTrigger asChild>
                  <div
                    className="cursor-pointer transition-all duration-300 hover:opacity-80"
                    style={{
                      width: `${item.percentage}%`,
                      backgroundColor: item.color,
                      minWidth: item.percentage > 0 ? "2px" : "0px",
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  sideOffset={8}
                  className="border-dark-border border bg-black"
                >
                  <div className="space-y-1">
                    <div className="font-semibold">{item.plan_name}</div>
                    <div className="text-xs opacity-90">
                      Usage: {item.usage.toLocaleString()} (
                      {item.percentage.toFixed(1)}%)
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            ))
          : endpointsWithColors.map((item, index) => (
              <Tooltip key={`tooltip-${item.name}-${index}`}>
                <TooltipTrigger asChild>
                  <div
                    className="cursor-pointer transition-all duration-300 hover:opacity-80"
                    style={{
                      width: `${item.percentage * 100}%`,
                      backgroundColor: item.color,
                      minWidth: item.percentage > 0 ? "2px" : "0px",
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  sideOffset={8}
                  className="border-dark-border border bg-black"
                >
                  <div className="space-y-1">
                    <div className="font-semibold">{item.name}</div>
                    <div className="text-xs opacity-90">
                      Usage: {item.value.toLocaleString()} (
                      {item.percentage.toFixed(1)}%)
                    </div>
                    {item.createdDate && (
                      <div className="text-xs opacity-75">
                        Created: {item.createdDate}
                      </div>
                    )}
                    {item.lastUsed && (
                      <div className="text-xs opacity-75">
                        Last used: {item.lastUsed}
                      </div>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            ))}
        {remainingPercentage > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="cursor-pointer bg-gray-600"
                style={{ width: `${remainingPercentage}%` }}
              />
            </TooltipTrigger>
            <TooltipContent
              side="top"
              sideOffset={8}
              className="border-dark-border border bg-black"
            >
              <div className="space-y-1">
                <div className="font-semibold">Remaining Quota</div>
                {isOverview && quotaTotal > 0 ? (
                  <>
                    <div className="text-xs opacity-90">
                      Remaining: {(quotaTotal - quotaUsed).toLocaleString()} /{" "}
                      {quotaTotal.toLocaleString()}
                    </div>
                    <div className="text-xs opacity-90">
                      ({remainingPercentage.toFixed(1)}%)
                    </div>
                  </>
                ) : (
                  <div className="text-xs opacity-90">
                    Unused: {remainingPercentage.toFixed(1)}%
                  </div>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* ✅ Toggle between legend or table */}
      {showTable ? (
        isOverview ? (
          <div className="scrollbar-thin overflow-x-auto">
            <table className="w-full table-fixed border-collapse text-sm">
              <thead>
                <tr className="text-black-500 text-left text-xs tracking-wide uppercase">
                  <th className="w-[30%] py-3 font-medium">Plan Name</th>
                  <th className="w-[20%] py-3 font-medium">API Keys</th>
                  <th className="w-[15%] py-3 font-medium">Usage</th>
                  <th className="w-[15%] py-3 font-medium">Start Date</th>
                  <th className="w-[15%] py-3 font-medium">End Date</th>
                </tr>
              </thead>
              <tbody>
                {subscriptionsWithColors.map((subscription, index) => (
                  <tr
                    key={`row-${subscription.plan_name}-${index}`}
                    className="bg-black-900/50 hover:bg-black-900/80 align-top transition-colors"
                  >
                    <td className="py-4 align-top font-bold text-white">
                      <div className="flex items-start">
                        <i
                          className="ri-circle-fill mr-2 text-xs"
                          style={{ color: subscription.color }}
                        ></i>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className="cursor-default truncate"
                              title={subscription.plan_name}
                            >
                              {subscription.plan_name}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            className="border-dark-border max-w-md border bg-black"
                          >
                            <div className="space-y-1">
                              <p className="text-xs font-semibold">
                                {subscription.plan_name}
                              </p>
                              <p className="text-xs opacity-75">
                                Entity Types: {subscription.endpoint_path}
                              </p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </td>

                    <td className="py-4 text-white">
                      <div className="flex flex-col gap-1">
                        {subscription.api_key
                          .split(", ")
                          .map((key, keyIndex) => (
                            <Tooltip key={`key-${keyIndex}`}>
                              <TooltipTrigger asChild>
                                <span
                                  className="cursor-default truncate text-xs"
                                  title={key}
                                >
                                  {key}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                className="border-dark-border max-w-md border bg-black"
                              >
                                <p className="text-xs">{key}</p>
                              </TooltipContent>
                            </Tooltip>
                          ))}
                      </div>
                    </td>

                    <td className="py-4">
                      <div className="space-y-1">
                        <div className="text-white">
                          {subscription.billing ? (
                            <span>
                              {subscription.billing.charge_breakdown[0]?.count?.toLocaleString() ||
                                0}
                            </span>
                          ) : (
                            <span>{subscription.usage.toLocaleString()}</span>
                          )}
                          {showPercentages && (
                            <span className="text-black-500 ml-1">
                              ({subscription.percentage.toFixed(1)}%)
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="py-4 text-white">
                      {formatTokenDate(subscription.subscription_start)}
                    </td>

                    <td className="py-4 text-white">
                      {formatTokenDate(subscription.subscription_end)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="scrollbar-thin overflow-x-auto">
            <table className="w-full table-fixed border-collapse text-sm">
              <thead>
                <tr className="text-black-500 text-left text-xs tracking-wide uppercase">
                  <th className="w-[30%] py-3 font-medium">API Key</th>
                  <th className="w-[15%] py-3 font-medium">Created Date</th>
                  <th className="w-[15%] py-3 font-medium">Last Used</th>
                  <th className="w-[20%] py-3 font-medium">Usage</th>
                  <th className="w-[10%] py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {endpointsWithColors.map((endpoint, index) => (
                  <tr
                    key={`row-${endpoint.name}-${index}`}
                    className="bg-black-900/50 hover:bg-black-900/80 transition-colors"
                  >
                    <td className="py-4 font-bold text-white">
                      <div className="flex items-center">
                        <i
                          className="ri-circle-fill mr-2 text-xs"
                          style={{ color: endpoint.color }}
                        ></i>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className="w-[200px] cursor-default truncate"
                              title={endpoint.name}
                            >
                              {endpoint.name}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            className="border-dark-border max-w-md border bg-black"
                          >
                            <p className="text-xs">{endpoint.name}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </td>

                    <td className="py-4 text-white">
                      {endpoint.createdDate || "—"}
                    </td>

                    <td className="py-4 text-white">
                      {endpoint.lastUsed || "—"}
                    </td>

                    <td className="py-4">
                      <div className="space-y-1">
                        <div className="text-white">
                          {showValues && (
                            <>
                              {endpoint.billing ? (
                                <span>
                                  {endpoint.billing.charge_breakdown[0]?.count?.toLocaleString()}
                                  &nbsp;
                                </span>
                              ) : (
                                <span>{endpoint.value.toLocaleString()} </span>
                              )}
                            </>
                          )}
                          {showPercentages && (
                            <>
                              {endpoint.billing ? (
                                <span>
                                  (
                                  {endpoint.billing.tier_usage_percentage.toFixed(
                                    1,
                                  )}
                                  % )
                                </span>
                              ) : (
                                <span>({endpoint.percentage.toFixed(1)}%)</span>
                              )}
                            </>
                          )}
                        </div>
                        <div className="h-1 w-[135px] overflow-hidden rounded bg-white/20">
                          <div
                            className="h-1 rounded bg-white"
                            style={{
                              width: `${endpoint.billing ? endpoint.billing.tier_usage_percentage : endpoint.percentage}%`,
                              backgroundColor: endpoint.color,
                            }}
                          />
                        </div>
                      </div>
                    </td>

                    <td className="py-4">
                      <div className="flex gap-3">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              className="bg-destructive hover:bg-destructive/60 gap-2 p-1.5 text-xs font-medium text-white hover:text-white"
                              variant="minimal"
                              icon
                              onClick={() => onRevokeToken?.(endpoint.id)}
                              disabled={onRevokeTokenLoading}
                            >
                              {onRevokeTokenLoading ? (
                                <>
                                  <Spinner
                                    size="md"
                                    className="stroke-white!"
                                  />
                                  Deactivating...
                                </>
                              ) : (
                                <>
                                  <i className="ri-close-circle-line text-md font-light"></i>
                                  Deactivate
                                </>
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top" sideOffset={4}>
                            <div className="text-xs">
                              Revoke access for {endpoint.name}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <div className="space-y-3 p-0 pt-0">
          {endpointsWithColors.map((endpoint, index) => (
            <Tooltip key={`legend-tooltip-${endpoint.name}-${index}`}>
              <TooltipTrigger asChild>
                <div
                  key={`legend-${endpoint.name}-${index}`}
                  className="-m-2 flex cursor-pointer items-center gap-3 space-y-5 rounded-md p-2 transition-colors hover:bg-white/5"
                >
                  <div className="flex flex-1 items-center">
                    <div className="max-w-[300px] truncate">
                      <i
                        className="ri-circle-fill mr-3 text-xs"
                        style={{ color: endpoint.color }}
                      ></i>
                      <span className="text-md font-bold -tracking-wide text-white">
                        {endpoint.name}
                      </span>
                    </div>
                    <div className="text-md pl-2 font-medium -tracking-wide text-white">
                      {showValues && (
                        <span>{endpoint.value.toLocaleString()}</span>
                      )}
                      {showPercentages && (
                        <span> ({endpoint.percentage.toFixed(1)}%)</span>
                      )}
                    </div>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent
                side="right"
                sideOffset={8}
                className="border-dark-border border bg-black"
              >
                <div className="space-y-1">
                  <div className="font-semibold">{endpoint.name}</div>
                  <div className="text-xs opacity-90">
                    Total usage: {endpoint.value.toLocaleString()}
                  </div>
                  <div className="text-xs opacity-90">
                    Percentage: {endpoint.percentage.toFixed(1)}%
                  </div>
                  {endpoint.createdDate && (
                    <div className="text-xs opacity-75">
                      Created: {endpoint.createdDate}
                    </div>
                  )}
                  {endpoint.lastUsed && (
                    <div className="text-xs opacity-75">
                      Last used: {endpoint.lastUsed}
                    </div>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      )}
    </div>
  );
}
